import { ProfileSection } from '@/pages/Profile/types';
import type { Encounter } from '@/types/encounter';
import { healthieAppointmentLink } from '@/utils/healthieLinks';

export interface EncounterChecklistItemLink {
  href: string;
  // Opens outside Dugout (Healthie), in the active tab when in the extension
  external: boolean;
}

const appointmentLink = (encounter: Encounter, host: string) => {
  const { appointment } = encounter;
  if (!appointment?.healthie_id || !appointment.start_at) return null;

  return {
    href: healthieAppointmentLink(appointment.healthie_id, appointment.start_at, host),
    external: true
  };
};

/**
 * Where a provider goes to resolve a checklist item, or null when there's
 * nowhere to send them (yet). Recording a new drug screen isn't a plain link,
 * since it may need confirming first; see useRecordDrugScreen.
 */
const encounterChecklistItemLink = ({
  itemKey,
  encounter,
  host
}: {
  itemKey: string;
  encounter: Encounter;
  host: string;
}): EncounterChecklistItemLink | null => {
  const personId = encounter.person?.id;

  switch (itemKey) {
    case 'mark_attendance':
      return appointmentLink(encounter, host);
    case 'enroll_member':
      // The profile scrolls to the section the fragment names
      return personId
        ? { href: `/people/${personId}#${ProfileSection.EPISODE_OF_CARE}`, external: false }
        : null;
    case 'complete_charting_note': {
      const charts = encounter.charts ?? [];
      const chart = charts.find((x) => x.is_billing_chart) ?? charts[0];
      const chartUrl = chart?.external_links.healthie.url;
      // Without a chart yet, the appointment is where one gets started
      return chartUrl ? { href: chartUrl, external: true } : appointmentLink(encounter, host);
    }
    case 'missing_information':
      return personId ? { href: `/people/${personId}/profile`, external: false } : null;
    case 'record_drug_screen': {
      const drugScreen = encounter.drug_screens?.[0];
      return personId && drugScreen
        ? { href: `/people/${personId}/drug-screens/${drugScreen.id}`, external: false }
        : null;
    }
    default:
      return null;
  }
};

export default encounterChecklistItemLink;
